import type { FeatureMiddleware } from '@cast/api-client';
import {
  demoClusterDetails,
  generateClusterManagementClusterComponents,
  generateClusterManagementClustersComponents,
  generateClusterManagementComponent,
  generateClusterManagementComponents,
  generateClusterManagementComponentStatus,
  generateClusterManagementFeatures,
  generateClusterManagementRunAction,
} from '@cast/fixtures';

import {
  getIsClusterDemoMode,
  getIsOpDemoMode,
} from '@cast/infrastructure-demo';

import { getOrgId, opDemoLoader } from '../utils';

export const clusterManagement: FeatureMiddleware<'clusterManagement'> = {
  listFeatures: async ({ next, resolve }) => {
    if (getIsClusterDemoMode() || getIsOpDemoMode()) {
      return resolve(generateClusterManagementFeatures());
    }
    return next();
  },
  getComponents: async ({ next, resolve }) => {
    if (getIsClusterDemoMode() || getIsOpDemoMode()) {
      return resolve(generateClusterManagementComponents());
    }
    return next();
  },
  getClustersComponents: async ({ next, resolve }) => {
    if (getIsOpDemoMode()) {
      const getClustersFixture = await opDemoLoader.loadFixture({
        feature: 'clusters',
        endpoint: 'getClusters',
      });

      if (getClustersFixture?.items?.[0]) {
        const demoCluster = getClustersFixture.items[0];

        return resolve(
          generateClusterManagementClustersComponents([
            {
              id: demoCluster.id!,
              name: demoCluster.name!,
              componentCount: 13,
              statuses: [
                {
                  status: 'FAILED',
                  count: 1,
                },
                {
                  status: 'WARNING',
                  count: 1,
                },
              ],
            },
          ])
        );
      }
    }

    if (getIsClusterDemoMode()) {
      const orgId = getOrgId();
      const demoCluster = await demoClusterDetails(orgId);

      return resolve(
        generateClusterManagementClustersComponents([
          {
            id: demoCluster.id,
            name: demoCluster.name,
            componentCount: 13,
            statuses: [
              {
                status: 'FAILED',
                count: 1,
              },
              {
                status: 'WARNING',
                count: 1,
              },
            ],
          },
        ])
      );
    }
    return next();
  },
  getClusterComponents: async ({ next, resolve }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return resolve(generateClusterManagementClusterComponents());
    }
    return next();
  },
  getComponent: async ({ args, next, resolve }) => {
    if (getIsOpDemoMode()) {
      const getClustersFixture = await opDemoLoader.loadFixture({
        feature: 'clusters',
        endpoint: 'getClusters',
      });

      if (getClustersFixture?.items?.[0]) {
        const demoCluster = getClustersFixture.items[0];
        const componentId = args[0].id;
        const components = generateClusterManagementComponents().components;
        const component = components.find((c) => c.id === componentId);

        return resolve(
          generateClusterManagementComponent(component, [
            {
              id: demoCluster.id!,
              name: demoCluster.name!,
              action: 'UPDATE',
              status: 'ACTION_REQUIRED',
              usedVersion: '0.0.1',
            },
          ])
        );
      }
    }

    if (getIsClusterDemoMode()) {
      const componentId = args[0].id;
      const components = generateClusterManagementComponents().components;
      const component = components.find((c) => c.id === componentId);
      const orgId = getOrgId();
      const demoCluster = await demoClusterDetails(orgId);

      return resolve(
        generateClusterManagementComponent(component, [
          {
            id: demoCluster.id,
            name: demoCluster.name,
            action: 'UPDATE',
            status: 'ACTION_REQUIRED',
            usedVersion: '0.0.1',
          },
        ])
      );
    }
    return next();
  },
  runComponentAction: async ({ next, resolve }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return resolve(
        generateClusterManagementRunAction({
          script: `echo "This is a demo script. Onboard a cluster to see the actual script"`,
        })
      );
    }
    return next();
  },
  getComponentStatus: async ({ next, resolve }) => {
    if (getIsOpDemoMode() || getIsClusterDemoMode()) {
      return resolve(
        generateClusterManagementComponentStatus({
          installedComponentId: 'installedComponentId',
          status: 'OK',
        })
      );
    }
    return next();
  },
};
